import { Fragment, isValidElement, ReactElement, RefObject } from 'react';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { ComboBoxItem } from '@/components/select/comboBoxTypes';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface ComboBoxFieldProps<T extends ComboBoxItem> {
  label?: string | ReactElement;
  hideLabel?: true;
  placeholder?: string;
  inputRef: RefObject<HTMLInputElement>;
  open: boolean;
  items: T[];
  isLoading: boolean;
  allowEmptySelection?: true;
  requireSelection?: true;
  showEmptyOption: boolean;
  displayValue: (item: T | T[] | null) => string;
  onFocus: () => void;
  onBlur: () => void;
  onAfterLeave: () => void;
  onDeselect: () => void;
  onClearQuery?: () => void;
  showEmptyState?: boolean;
  modal?: boolean;
}

const ComboBoxField = <T extends ComboBoxItem>({
  label,
  hideLabel,
  placeholder,
  inputRef,
  open,
  items,
  isLoading,
  allowEmptySelection,
  requireSelection,
  showEmptyOption,
  displayValue,
  onFocus,
  onBlur,
  onAfterLeave,
  onDeselect,
  onClearQuery,
  showEmptyState = false,
  modal = true,
}: ComboBoxFieldProps<T>) => {
  const hasEmptyOption = showEmptyOption && allowEmptySelection && !requireSelection;
  const hasStatusMessage = items.length === 0 && showEmptyState;
  const hasDropdownContent = items.length > 0 || hasEmptyOption || hasStatusMessage;

  return (
    <>
      {!hideLabel && (
        <Label className={label && isValidElement(label) ? '' : 'block mb-1'}>{label}</Label>
      )}
      <div className="relative">
        <div className="relative w-full cursor-default border rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 flex items-center">
          <ComboboxInput<T | T[] | null>
            ref={inputRef}
            onFocus={onFocus}
            onBlur={onBlur}
            className={`w-full border-none py-0.5 pl-1 ${onClearQuery ? 'pr-16' : 'pr-10'} leading-5 text-gray-900 focus:ring-0 focus:outline-none`}
            displayValue={displayValue}
            placeholder={isLoading ? 'Loading ...' : placeholder || ''}
          />
          {onClearQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onClearQuery}
              className="absolute inset-y-0 right-8 flex items-center px-1 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
          {isLoading && (
            <SpinnerIcon className={`w-5 h-5 absolute ${onClearQuery ? 'right-16' : 'right-10'}`} />
          )}
        </div>
        <Transition
          as={Fragment}
          show={open && hasDropdownContent}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={onAfterLeave}
        >
          <ComboboxOptions
            modal={modal}
            className={
              hasDropdownContent
                ? 'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'
                : 'hidden'
            }
          >
            {hasStatusMessage ? (
              <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                {isLoading ? 'Loading ...' : 'Nothing found'}
              </div>
            ) : (
              <>
                {hasEmptyOption ? (
                  <ComboboxOption
                    key="empty"
                    value={null}
                    onClick={onDeselect}
                    className={({ focus }) =>
                      `${focus ? 'text-white bg-blue-600' : 'text-gray-900'} cursor-pointer select-none relative py-4`
                    }
                  >
                    {({ focus, selected }) => (
                      <>
                        {selected ? (
                          <span
                            className={`${focus ? 'text-white' : 'text-gray-900'} absolute inset-y-0 left-0 flex items-center pl-3`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ) : null}
                {items.map((item) => (
                  <ComboboxOption
                    key={item.id}
                    className={({ focus }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        focus ? 'bg-blue-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={item}
                  >
                    {({ selected, focus }) => (
                      <>
                        <span
                          className={`${selected ? 'font-medium' : 'font-normal'} block truncate`}
                        >
                          {item.name}
                        </span>
                        {selected ? (
                          <span
                            className={`${focus ? 'text-white' : 'text-gray-900'} absolute inset-y-0 left-0 flex items-center pl-3`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))}
              </>
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </>
  );
};

export default ComboBoxField;
